import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { TerminateFolderModalStore } from './terminate-folder-modal.store';
import { FormBuilder, Validators } from '@angular/forms';
import { isInvalid } from '../../../../shared/helpers/validate';
import { FolderStatusEnum } from '../../../../../../../../types/src/services/enum/folder-status.enum';
import { selectFolder } from '../../../store/person/person.selectors';
import { Dialog } from '@openng/optimus-ui/dialog';
import { TranslatePipe } from '@ngx-translate/core';

import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { TextAreaInputComponent } from '../../../../../components/kit/input/text-area-input/text-area-input.component';
import { CheckInputComponent } from '../../../../../components/kit/input/check-input/check-input.component';
import { ErrorFloatMessageComponent } from '../../../../../components/kit/float-message/error-float-message.component';
import { CancelButtonComponent } from '../../../../../components/kit/button/cancel-button.component';
import { ConfirmButtonComponent } from '../../../../../components/kit/button/confirm-button.component';
import { contentGlobalSuccess } from '../../../content.error';
import { routeParams } from '../../../../shared/shared.global-service';

@Component({
    selector: 'app-terminate-folder-modal',
    templateUrl: 'terminate-folder-modal.component.html',
    styleUrl: 'terminate-folder-modal.component.scss',
    providers: [TerminateFolderModalStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    Dialog,
    TranslatePipe,
    DateInputComponent,
    TextAreaInputComponent,
    CheckInputComponent,
    ErrorFloatMessageComponent,
    CancelButtonComponent,
    ConfirmButtonComponent
],
})
export class TerminateFolderModalComponent extends SubscriptionComponent {

    protected readonly store = inject(TerminateFolderModalStore);
    private readonly fb = inject(FormBuilder);

    protected visible = false;
    protected err: string | null = null;

    today = new Date();

    protected form = this.fb.group({
        dateFrom: this.fb.control<Date | null>(new Date()),
        message: this.fb.control<string>(''),
        isDead: this.fb.control<boolean>(false),
        isClosed: this.fb.control<boolean>(false),
        dateDeath: this.fb.control<Date | null>(new Date()),
    });

    constructor() {
        super();
        this.subs.push(
            this.store.store!.select(selectFolder).subscribe((it) => {
                const applicant = it?.status === FolderStatusEnum.APPLICANT;
                const dead = it?.status === FolderStatusEnum.DEAD;

                if (applicant || dead) {
                    this.form.controls.dateFrom.setValidators(Validators.required);
                    this.form.controls.dateDeath.clearValidators();
                } else {
                    this.form.controls.dateDeath.setValidators(Validators.required);
                    this.form.controls.dateFrom.clearValidators();
                }
            }),
        );
    }

    public show(visible: boolean = true): void {
        this.visible = visible;
    }

    protected async save(): Promise<void> {
        this.err = null;
        if (isInvalid(this.form)) return;
        const applicant = this.store.isApplicant();
        const dead = this.store.isDead();
        if (this.form.value.dateDeath) {
            if (this.form.value.dateDeath! > new Date()) {
                this.err = 'folder.terminate.message.deathInFuture';
                return;
            }
        }

        const folderId = Number(routeParams.folderId());
        if (applicant) {
            const response = await this.store.api.terminateFolder({
                id: folderId,
                message: this.form.value.message || null,
                dateFrom: this.form.value.dateFrom!.toISOString(),
                dateDeath: this.form.value.isDead ? this.form.value.dateDeath?.toISOString() || null : null,
            });

            if (response?.ok) {
                this.form.value.isDead ? contentGlobalSuccess.next('folder.terminate.message.dead') : contentGlobalSuccess.next('folder.terminate.message.terminated');
                this.show(false);
            }
        } else if (dead) {
            const response = await this.store.api.terminateFolder({
                id: folderId,
                message: this.form.value.message || null,
                dateFrom: this.form.value.dateFrom!.toISOString(),
            });

            if (response?.ok) {
                contentGlobalSuccess.next('folder.terminate.message.terminated');
                this.show(false);
            }
        } else {
            const response = await this.store.api.terminateFolder({
                id: folderId,
                message: this.form.value.message || null,
                dateDeath: this.form.value.dateDeath!.toISOString(),
                dateFrom: this.form.value.isClosed ? this.form.value.dateFrom?.toISOString() || null : null,
            });

            if (response?.ok) {
                this.form.value.isClosed ? contentGlobalSuccess.next('folder.terminate.message.terminated') : contentGlobalSuccess.next('folder.terminate.message.dead');
                this.show(false);
            }
        }
    }

}
